import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine, 
  faFileText, 
  faTools, 
  faCalendarDay, 
  faUsers, 
  faSync, 
  faBook, 
  faMoon,
  faFire,
  faLaptop,
  faMobileAlt,
  faBullseye,
  faTrophy,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';

const Features = ({ title, subtitle, features, theme }) => {
  // Map icon names to FontAwesome icons
  const getIcon = (iconName) => {
    const iconMap = {
      'chart-line': faChartLine,
      'file-text': faFileText,
      'tools': faTools,
      'calendar-day': faCalendarDay,
      'users': faUsers,
      'sync': faSync,
      'book': faBook,
      'moon': faMoon,
      'fire': faFire,
      'laptop': faLaptop,
      'mobile-alt': faMobileAlt,
      'bullseye': faBullseye,
      'trophy': faTrophy,
      'question-circle': faQuestionCircle
    };
    return iconMap[iconName] || faBook; // fallback to book icon
  };

  const styles = {
    container: {
      paddingTop: '15px',
      paddingBottom: '15px',
      scrollMarginTop: '80px', // Add scroll margin to account for fixed header
      textAlign: 'center',
      position: 'relative'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem'
    },
    title: {
      fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
      fontWeight: '800',
      color: theme?.text?.primary,
      marginBottom: '0.75rem'
    },
    subtitle: {
      fontSize: 'clamp(0.95rem, 2.5vw, 1.125rem)',
      color: theme?.text?.secondary,
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: '1.6',
      padding: '0 0.5rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
      gap: 'clamp(1rem, 3vw, 2rem)'
    },
    card: {
      padding: 'clamp(0.75rem, 2vw, 1.25rem)',
      background: theme?.background?.card,
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      border: `1px solid ${theme?.borders?.primary || '#E5E7EB'}`,
      transition: 'all 0.3s ease',
      position: 'relative'
    },
    cardHover: {
      transform: 'translateY(-5px)',
      boxShadow: '0 12px 25px rgba(0, 0, 0, 0.1)'
    },
    icon: {
      width: 'clamp(40px, 8vw, 50px)',
      height: 'clamp(40px, 8vw, 50px)',
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[500]}, ${theme?.primary?.orange?.[500]})`,
      borderRadius: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0.5rem',
      color: theme?.neutral?.white || 'white',
      fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
      overflow: 'hidden'
    },
    emoji: {
      fontSize: '1.5rem',
      lineHeight: 1,
      display: 'inline-block'
    },
    cardTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '0.75rem',
      color: theme?.text?.primary
    },
    cardDescription: {
      color: theme?.text?.secondary,
      lineHeight: '1.5',
      marginBottom: '1rem',
      fontSize: '0.875rem'
    },
    highlight: {
      width: 'fit-content',
      background: theme?.primary?.orange?.[200],
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: '600',
      color: theme?.primary?.orange?.[600],
      display: 'inline-block'
    },
    highlightEmoji: {
      fontSize: '0.9em',
      marginRight: '0.4em',
      verticalAlign: 'middle',
      display: 'inline-block',
      lineHeight: 1
    }
  };

  return (
    <section id="features" style={styles.container}>
      <div style={styles.content}>
        <div style={styles.header}>
          <h2 style={styles.title}>{title}</h2>
          <p style={styles.subtitle}>{subtitle}</p>
        </div>
        <div style={styles.grid}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={styles.card}
            >
              <div style={styles.icon}>
                <FontAwesomeIcon icon={getIcon(feature.icon)} style={{ fontSize: '1.5rem' }} />
              </div>
              <h3 style={styles.cardTitle}>{feature.title}</h3>
              <p style={styles.cardDescription}>{feature.description}</p>
              {feature.featureHighlightText && (
                <div style={styles.highlight}>
                  {feature.featureHighlightIcon && (
                    <FontAwesomeIcon 
                      icon={getIcon(feature.featureHighlightIcon)} 
                      style={styles.highlightEmoji} 
                    />
                  )}
                  {feature.featureHighlightText}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features; 